import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import tagIcon from "../../images/newSvg.svg";
import { useSnackbarContext } from "../../component/SnackbarContext";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import { currencies } from "../../Utility/currencies";

// code -> symbol lookup (e.g. USD -> $, INR -> ₹) reused from the shared currency list.
const CURRENCY_SYMBOL = currencies.reduce((acc, c) => {
    acc[c.code] = c.symbol;
    return acc;
}, {});

const emptyTabData = {
    daily: null,
    weekly: null,
    monthly: null,
};

// Tab id (also the backend/tab-state value) -> i18n key for its visible label.
const TAB_LABEL_KEYS = {
    daily: "rakeback.tabDaily",
    weekly: "rakeback.tabWeekly",
    monthly: "rakeback.tabMonthly",
};
// The user's wallet currency (ISO code, e.g. USD/INR). Kept in sync from redux by the
// component below so amounts render in the player's own currency instead of a fixed coin.
let displayCurrency = "USD";
// Shows up to 4 decimals so small amounts like 0.045 aren't rounded away.
const formatMoney = (value) => {
    const amount = Number(value || 0);
    const rounded = Number(amount.toFixed(4));
    const decimalDigits = (rounded.toString().split(".")[1] || "").length;
    // Prefer the currency symbol ($, ₹, €); fall back to the code if unmapped.
    const symbol = CURRENCY_SYMBOL[displayCurrency] || `${displayCurrency} `;
    return `${symbol}${rounded.toFixed(Math.max(2, decimalDigits))}`;
};
const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatDate = (value) => value || "-";
const formatDisplayDate = (value) => {
    if (!value) return "-";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
// claimDeadline = last claim + 24h; null means not in cooldown.
const getRemainingTimeLabel = (deadline, t) => {
    if (!deadline) return "";
    const deadlineTime = new Date(String(deadline).replace(" ", "T")).getTime();
    if (Number.isNaN(deadlineTime)) return "";

    const diffMs = deadlineTime - Date.now();
    if (diffMs <= 0) return t('rakeback.claimAvailableNow');

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return t('rakeback.nextClaimInHours', { hours, minutes, seconds });
    if (minutes > 0) return t('rakeback.nextClaimInMinutes', { minutes, seconds });
    return t('rakeback.nextClaimInSeconds', { seconds });
};

const numberValue = (...values) => {
    const value = values.find((item) => item !== undefined && item !== null && item !== "");
    const parsedValue = Number(value || 0);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

// Backend status value -> i18n key for the label shown to the player. The status values
// themselves stay untranslated because they are compared against below.
const STATUS_LABEL_KEYS = {
    CLAIMED: "rakeback.statusClaimed",
    CLAIMABLE: "rakeback.statusClaimable",
    EXPIRED: "rakeback.statusExpired",
    NONE: "",
};

// Backend day -> { key, dateLabel, subtitle, lines } for the day rows.
// A day that was claimed and then earned more (carry-forward) is shown as TWO lines —
// the part already claimed and the part still open — never as one merged total, so the
// claimed amount can't be read as claimable again.
const mapRakebackDay = (day, t) => {
    const statusLabel = (statusKey) =>
        STATUS_LABEL_KEYS[statusKey] ? t(STATUS_LABEL_KEYS[statusKey]) : "";
    const earned = numberValue(day.rakebackEarned);
    const claimed = numberValue(day.claimedTodayAmount);
    const claimable = numberValue(day.claimableRakebackAmount);
    const remainder = Math.max(earned - claimed, 0);
    const status = day.status || (earned > 0 ? "EXPIRED" : "NONE");

    const lines = [];
    if (claimed > 0 && remainder > 0) {
        lines.push({ amount: formatMoney(claimed), label: statusLabel("CLAIMED"), isPositive: true });
        if (claimable > 0) {
            lines.push({ amount: `+${formatMoney(claimable)}`, label: statusLabel("CLAIMABLE"), isPositive: true });
        } else if (status === "EXPIRED") {
            lines.push({ amount: `+${formatMoney(remainder)}`, label: statusLabel("EXPIRED"), isPositive: false });
        } else {
            // Earned after today's claim — becomes claimable tomorrow via carry-forward.
            lines.push({ amount: `+${formatMoney(remainder)}`, label: t('rakeback.statusClaimableTomorrow'), isPositive: true });
        }
    } else {
        const isPositive = status === "CLAIMED" || status === "CLAIMABLE";
        lines.push({ amount: `+${formatMoney(earned)}`, label: statusLabel(status), isPositive });
    }

    return {
        key: day.date || formatDisplayDate(day.date),
        dateLabel: formatDisplayDate(day.date),
        subtitle: status === "NONE" ? t('rakeback.noPlay') : "",
        lines,
    };
};

const getDailySummary = (daily = {}) => {
    const depositsToday = numberValue(daily.todayDeposit);
    const withdrawalsToday = numberValue(daily.todayWithdraw);
    // Careful: fields are swapped vs the usual convention — todayDeposit = bet/play,
    // todayWithdraw = win/payout. Net loss is therefore deposit − withdraw. Intentional —
    // do not flip this.
    const netLoss = Math.max(depositsToday - withdrawalsToday, 0);
    const rakebackEarned = numberValue(daily.rakebackEarned);

    // Claimable is server-authoritative and shown even during the cooldown, so the daily
    // header always matches the weekly "Remaining". Whether it can be claimed RIGHT NOW is
    // a separate gate (canClaim) that only controls the button, never the amount.
    const totalClaimable = numberValue(daily.claimableRakebackAmount);

    // Yesterday's unclaimed rakeback carried into today (display only).
    const carryForward = numberValue(daily.carryforward);

    // Earned after today's claim; becomes claimable tomorrow via carry-forward.
    const pendingRakeback = numberValue(daily.pendingRakeback);

    return {
        depositsToday,
        withdrawalsToday,
        purchasedToday: numberValue(daily.todayPurchased),
        netLoss,
        rakebackRate: numberValue(daily.rakebackRate),
        rakebackEarned,
        totalClaimable,
        carryForward,
        pendingRakeback,
    };
};



const StatCard = ({ item }) => (
    <Box
        sx={{
            flex: 1,
            minWidth: item[0].length > 20 ? "100%" : 120,
            p: 1.5,
            borderRadius: 2,
            background: "linear-gradient(135deg, #f4f1e9, #fffdf7)",
            border: "1px solid #ebe5d7",
        }}
    >
        <Typography sx={{ color: "#5a5a5a", fontSize: 12, textTransform: "uppercase" }}>
            {item[0]}
        </Typography>
        <Typography sx={{ color: "#101010", fontSize: 26, lineHeight: 1.25 }}>
            {item[1]}
        </Typography>
        <Typography sx={{ color: "#1d1d1d", fontSize: 12 }}>{item[2]}</Typography>
    </Box>
);

const DetailRow = ({ row }) => {
    const [, value, type] = row;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                py: 0.8,
                borderBottom: "1px solid #ded8cb",
                "&:last-of-type": { borderBottom: 0 },
            }}
        >
            <Typography sx={{ color: "#111", fontSize: 14 }}>{row[0]}</Typography>
            {type === "chip" ? (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        height: 24,
                        color: "#0d5e3f",
                        backgroundColor: "#dff4eb",
                        fontSize: 12,
                    }}
                />
            ) : (
                <Typography
                    sx={{
                        color: type === "positive" ? "#159063" : "#111",
                        fontSize: 14,
                        fontWeight: type === "strong" ? 700 : 400,
                        textAlign: "right",
                    }}
                >
                    {value}
                </Typography>
            )}
        </Box>
    );
};

const TierRatesPanel = ({ currentTierName, tiers }) => {
    const { t } = useTranslation();

    return (
    <Box sx={{ p: { xs: 1.75, sm: 2 }, color: "#111" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.75 }}>{t('rakeback.ratesTitle')}</Typography>
        <Typography sx={{ color: "#444", fontSize: 12, lineHeight: 1.45, mb: 1.5 }}>
            {t('rakeback.ratesSubtitle')}
        </Typography>

        <Box sx={{ borderRadius: 2, border: "1px solid #e5ded0", overflow: "hidden" }}>
            {tiers.map((tier) => {
                const isCurrent = currentTierName?.toLowerCase() === tier.name.toLowerCase();
                return (
                    <Box
                        key={tier.name}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 90px",
                            gap: 1,
                            alignItems: "center",
                            px: 1.5,
                            py: 1,
                            backgroundColor: isCurrent ? "#eff8ff" : "#fff",
                            borderBottom: "1px solid #eee8dc",
                            "&:last-of-type": { borderBottom: 0 },
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: tier.color }} />
                            <Typography sx={{ fontSize: 13, fontWeight: isCurrent ? 800 : 500 }}>
                                {tier.name}
                            </Typography>
                            {isCurrent && (
                                <Typography sx={{ color: "#0b6c4b", fontSize: 11, fontWeight: 800 }}>
                                    {t('rakeback.you')}
                                </Typography>
                            )}
                        </Box>
                        <Typography sx={{ color: "#0b5d9f", fontSize: 13, fontWeight: 800, textAlign: "right" }}>
                            {formatPercent(tier.rakebackPercent)}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    </Box>
    );
};

const RakebackPromotion = ({ isAuthenticated, onLogin, GetUserById, banner }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarContext();
    const authState = useSelector((state) => state.auth);
    // Player's wallet currency; drives every formatMoney() call in this screen.
    const currency = authState?.IsLoginData?.currency || "USD";
    displayCurrency = currency;
    const [activeTab, setActiveTab] = useState("daily");
    const [open, setOpen] = useState(false);
    const [rakebackData, setRakebackData] = useState(emptyTabData);
    const [rakebackConfigs, setRakebackConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [resetTimerLabel, setResetTimerLabel] = useState("");
    const [showTierRates, setShowTierRates] = useState(false);

    const buildTabData = useMemo(() => {
        const daily = rakebackData.daily || {};
        const weekly = rakebackData.weekly || {};
        const monthly = rakebackData.monthly || {};
        const dailySummary = getDailySummary(daily);
        const weeklyDays = weekly.weekStatus || [];
        const weeklyEarned = numberValue(weekly.earnedThisWeek);
        const weeklyClaimed = numberValue(weekly.paidOut);
        const weeklyRemaining = numberValue(weekly.remaining);
        const monthlyEarned = numberValue(monthly.totalRakebackEarned);
        const monthlyClaimed = numberValue(monthly.totalRakebackClaimed);
        const monthlyExpired = numberValue(monthly.totalFlushed);
        const monthlyRemaining = numberValue(monthly.remaining);
        // Days come newest-first from the backend, so the range is [last, first].
        const weeklyFirstDate = weeklyDays[weeklyDays.length - 1]?.date;
        const weeklyLastDate = weeklyDays[0]?.date;
        const weeklyPeriodValue = weeklyFirstDate && weeklyLastDate
            ? `${formatDate(weeklyFirstDate)} - ${formatDate(weeklyLastDate)}`
            : "-";
        const hasNoGameplay =
            dailySummary.netLoss <= 0 &&
            dailySummary.totalClaimable <= 0 &&
            numberValue(daily.claimedTodayAmount) <= 0;

        return {
            daily: {
                periodLabel: t('rakeback.period'),
                periodValue: daily.date
                    ? t('rakeback.todayDate', { date: formatDisplayDate(daily.date) })
                    : "-",
                metrics: [
                    [t('rakeback.wageredToday'), formatMoney(dailySummary.withdrawalsToday)],
                    [t('rakeback.wonToday'), formatMoney(dailySummary.depositsToday)],
                    [t('rakeback.depositToday'), formatMoney(dailySummary.purchasedToday)],
                ],
                rows: [
                    [
                        t('rakeback.dailyRakebackRate'),
                        `${daily.packageName || t('rakeback.tier')} - ${formatPercent(dailySummary.rakebackRate)}`,
                        "chip",
                    ],
                    [t('rakeback.rakebackEarned'), `+${formatMoney(dailySummary.rakebackEarned)}`, "positive"],
                    ...(dailySummary.carryForward > 0
                        ? [[t('rakeback.carriedForwardYesterday'), `+${formatMoney(dailySummary.carryForward)}`, "positive"]]
                        : []),
                    [t('rakeback.totalClaimable'), formatMoney(dailySummary.totalClaimable), "strong"],
                ],
                notice: daily.canClaim !== false && dailySummary.totalClaimable > 0
                    ? t('rakeback.noticeAvailableNow')
                    : dailySummary.totalClaimable > 0 && daily.claimDeadline
                        ? t('rakeback.noticeWaitForTimer')
                        : hasNoGameplay
                            ? t('rakeback.playToEarn')
                            : daily.statusLabel === "Claimed Today"
                                ? t('rakeback.noticeAlreadyClaimed')
                                : daily.statusLabel || t('rakeback.noticeAfterScPlay'),
            },
            weekly: {
                periodLabel: t('rakeback.weekOf'),
                periodValue: weeklyPeriodValue,
                cards: [
                    [t('rakeback.earned'), formatMoney(weeklyEarned), t('rakeback.thisWeek')],
                    [t('rakeback.claimed'), formatMoney(weeklyClaimed), t('rakeback.thisWeek')],
                    [t('rakeback.remaining'), formatMoney(weeklyRemaining), t('rakeback.unclaimed')],
                ],
                rows: [
                    [t('rakeback.weeklyRakebackRate'), `${daily.packageName || t('rakeback.tier')} - ${formatPercent(dailySummary.rakebackRate)}`, "chip"],
                    [t('rakeback.earnedThisWeek'), `+${formatMoney(weeklyEarned)}`, "positive"],
                    [t('rakeback.paidOutDailyClaims'), formatMoney(weeklyClaimed)],
                    [
                        t('rakeback.remainingThisWeek'),
                        formatMoney(weeklyRemaining),
                        "strong",
                    ],
                ],
                days: weeklyDays.map((day) => mapRakebackDay(day, t)),
                notice: t('rakeback.noticeOncePerDay'),
            },
            monthly: {
                periodLabel: t('rakeback.monthOf'),
                periodValue: monthly.month || "-",
                cards: [
                    [t('rakeback.earned'), formatMoney(monthlyEarned), t('rakeback.thisMonth')],
                    [t('rakeback.claimed'), formatMoney(monthlyClaimed), t('rakeback.credited')],
                    [t('rakeback.expired'), formatMoney(monthlyExpired), t('rakeback.pastUnclaimed')],
                ],
                rows: [
                    [t('rakeback.totalEarned'), formatMoney(monthlyEarned), "positive"],
                    [t('rakeback.totalClaimed'), formatMoney(monthlyClaimed)],
                    [t('rakeback.expired'), formatMoney(monthlyExpired)],
                    [
                        t('rakeback.remaining'),
                        formatMoney(monthlyRemaining),
                        "strong",
                    ],
                ],
                days: (monthly.days || []).map((day) => mapRakebackDay(day, t)),
                notice: t('rakeback.noticeMonthlySummary'),
            },
        };
    }, [rakebackData, currency, t]);

    const activeData = buildTabData[activeTab];
    const dailySummary = getDailySummary(rakebackData.daily || {});
    const dailyAmount = dailySummary.totalClaimable;
    const rakebackDisabled = rakebackData.daily?.rakebackEnabled === false;
    const canClaim = !rakebackDisabled && dailyAmount > 0 && rakebackData.daily?.canClaim !== false;
    const isClaimedToday =
        rakebackData.daily?.status === "CLAIMED" ||
        rakebackData.daily?.statusLabel === "Claimed Today" ||
        numberValue(rakebackData.daily?.claimedTodayAmount) > 0;
    const hasNoDailyGameplay =
        !rakebackDisabled &&
        dailySummary.netLoss <= 0 &&
        Number(dailyAmount || 0) <= 0 &&
        !isClaimedToday;
    const canSubmitClaim = canClaim && Number(dailyAmount || 0) > 0;
    const inClaimCooldown = !rakebackDisabled && Boolean(rakebackData.daily?.claimDeadline);
    const claimButtonLabel = !isAuthenticated
        ? t('rakeback.logInToClaim')
        : rakebackDisabled
            ? t('rakeback.rakebackUnavailable')
            : claiming
                ? t('rakeback.claiming')
                : hasNoDailyGameplay
                    ? t('rakeback.playToEarnShort')
                    : inClaimCooldown
                        ? (resetTimerLabel || t('rakeback.claimAgainSoon'))
                        : canSubmitClaim
                            ? t('rakeback.claimAmountNow', { amount: formatMoney(dailyAmount) })
                            : t('rakeback.noRakebackAvailable');
    const currentTierName = rakebackData.daily?.packageName || t('rakeback.tierPending');
    const tierRows = useMemo(() => {
        if (!Array.isArray(rakebackConfigs) || rakebackConfigs.length === 0) {
            return [];
        }

        const colors = ["#9ccc65", "#64a6df", "#e2a532", "#a89cf2", "#5ccba7"];
        return rakebackConfigs
            .filter((config) => config?.Is_active !== 0)
            .sort((a, b) => Number(a.level || 0) - Number(b.level || 0))
            .map((config, index) => ({
                name: config.packagename || t('rakeback.tierLevel', { level: config.level }),
                rakebackPercent: Number(config.rakeback_percentage || 0),
                color: colors[index % colors.length],
            }));
    }, [rakebackConfigs, t]);

    // If a higher VIP tier exists with a better rakeback rate, show how much MORE today's
    // rakeback would be at that next tier — an upgrade incentive on the daily tab.
    const nextTierUpgrade = useMemo(() => {
        if (!tierRows.length || dailySummary.rakebackRate <= 0) return null;
        const idx = tierRows.findIndex(
            (t) => t.name?.toLowerCase() === currentTierName?.toLowerCase()
        );
        if (idx === -1 || idx >= tierRows.length - 1) return null; // unknown or already top tier
        const next = tierRows[idx + 1];
        if (!next || next.rakebackPercent <= dailySummary.rakebackRate) return null;

        const projectFromEarned = (earned) => {
            const currentEarned = numberValue(earned);
            if (currentEarned <= 0) return 0;
            const eligibleBase = (currentEarned * 100) / dailySummary.rakebackRate;
            return (eligibleBase * next.rakebackPercent) / 100;
        };

        const projectDays = (days = []) =>
            days.reduce((sum, day) => sum + projectFromEarned(day?.rakebackEarned), 0);

        let periodLabel = t('rakeback.periodToday');
        let currentEarned = dailySummary.rakebackEarned;
        let nextEarned = dailySummary.netLoss > 0
            ? (dailySummary.netLoss * next.rakebackPercent) / 100
            : projectFromEarned(currentEarned);

        if (activeTab === "weekly") {
            const weekly = rakebackData.weekly || {};
            const weeklyDays = weekly.weekStatus || [];
            periodLabel = t('rakeback.periodThisWeek');
            currentEarned = numberValue(weekly.earnedThisWeek);
            nextEarned = projectDays(weeklyDays);
        }

        if (activeTab === "monthly") {
            const monthly = rakebackData.monthly || {};
            periodLabel = t('rakeback.periodThisMonth');
            currentEarned = numberValue(monthly.totalRakebackEarned);
            nextEarned = projectDays(monthly.days || []);
        }

        if (currentEarned <= 0 && nextEarned <= 0) return null;

        return {
            name: next.name,
            rate: next.rakebackPercent,
            periodLabel,
            nextEarned,
            delta: Math.max(nextEarned - currentEarned, 0),
        };
    }, [
        activeTab,
        tierRows,
        currentTierName,
        dailySummary.rakebackRate,
        dailySummary.netLoss,
        dailySummary.rakebackEarned,
        rakebackData.weekly,
        rakebackData.monthly,
        t,
    ]);

    const headerStatus = useMemo(() => {
        if (activeTab === "daily") return t('rakeback.headerNewTiers');
        if (activeTab === "weekly") return t('rakeback.thisWeek');
        return t('rakeback.headerBonusStack');
    }, [activeTab, t]);

    const handleClaim = () => {
        if (!isAuthenticated && onLogin) {
            onLogin();
            return;
        }
        claimRakeback();
    };

    const fetchRakeback = async () => {
        if (!isAuthenticated) return;

        setLoading(true);
        try {
            const [dailyResult, weeklyResult, monthlyResult, configResult] = await Promise.allSettled([
                getAllData(endPoints.api.RAKEBACK_DAILY),
                getAllData(endPoints.api.RAKEBACK_WEEKLY),
                getAllData(endPoints.api.RAKEBACK_MONTHLY),
                getAllData(endPoints.api.GET_VIP_PACKAGES),
            ]);
            const dailyResponse = dailyResult.status === "fulfilled" ? dailyResult.value : null;
            const weeklyResponse = weeklyResult.status === "fulfilled" ? weeklyResult.value : null;
            const monthlyResponse = monthlyResult.status === "fulfilled" ? monthlyResult.value : null;
            const configResponse = configResult.status === "fulfilled" ? configResult.value : null;

            // Surface every failed tab (not just daily) in one message, preferring the
            // backend's own error message when it sent one.
            const tabResponses = { daily: dailyResponse, weekly: weeklyResponse, monthly: monthlyResponse };
            const failedTabs = Object.keys(tabResponses).filter(
                (tab) => tabResponses[tab]?.status !== "success"
            );
            if (failedTabs.length > 0) {
                const firstFailed = tabResponses[failedTabs[0]];
                showErrorSnackbar(
                    firstFailed?.data?.message ||
                    firstFailed?.message ||
                    t('rakeback.failedToLoadTabs', {
                        tabs: failedTabs.map((tab) => t(TAB_LABEL_KEYS[tab])).join(", "),
                    })
                );
            }

            setRakebackData({
                daily: dailyResponse?.status === "success" ? dailyResponse?.data : null,
                weekly: weeklyResponse?.status === "success" ? weeklyResponse?.data : null,
                monthly: monthlyResponse?.status === "success" ? monthlyResponse?.data : null,
            });
            setRakebackConfigs(
                Array.isArray(configResponse?.data)
                    ? configResponse.data
                    : Array.isArray(configResponse)
                        ? configResponse
                        : []
            );
        } catch (error) {
            console.error(error);
            showErrorSnackbar(
                error?.response?.data?.message ||
                error?.message ||
                t('rakeback.failedToLoadDetails')
            );
        } finally {
            setLoading(false);
        }
    };

    const claimRakeback = async () => {
        if (!canClaim) {
            showErrorSnackbar(rakebackData.daily?.statusLabel || t('rakeback.notClaimableNow'));
            return;
        }
        if (!canSubmitClaim) {
            showErrorSnackbar(t('rakeback.nothingToClaimNow'));
            return;
        }

        setClaiming(true);
        try {
            // No body needed — the server computes the claim amount itself.
            const response = await addData(endPoints.api.RAKEBACK_CLAIM, {});
            if (response?.data?.status === "success") {
                showSuccessSnackbar(response?.data?.message || t('rakeback.claimSuccess'));
                // Refresh the user so the SC wallet balance in the header reflects the credit.
                await GetUserById?.();
                // Auto-close after a successful claim; reopening re-fetches fresh rakeback data
                // via the open effect, so no explicit refetch is needed here.
                setOpen(false);
            } else {
                showErrorSnackbar(response?.data?.message || t('rakeback.claimFailed'));
            }
        } catch (error) {
            console.error(error);
            showErrorSnackbar(
                error?.response?.data?.message ||
                error?.message ||
                t('rakeback.claimFailedNoDeduction')
            );
            // Re-sync so the button reflects the true server state (e.g. it was actually
            // claimed, or the daily window changed) rather than a stale claimable amount.
            fetchRakeback();
        } finally {
            setClaiming(false);
        }
    };

    useEffect(() => {
        if (open) {
            setShowTierRates(false);
            fetchRakeback();
        }
    }, [open, isAuthenticated]);

    useEffect(() => {
        if (!open) return undefined;

        const deadline = rakebackData.daily?.claimDeadline;
        let refreshed = false;
        const updateTimer = () => {
            setResetTimerLabel(getRemainingTimeLabel(deadline, t));
            // When the countdown reaches zero, pull fresh data once so the claimable amount
            // and the claim button update without the user closing and reopening the dialog.
            // (The refetched data has no claimDeadline, which re-runs this effect and stops the timer.)
            if (!refreshed && deadline) {
                const deadlineTime = new Date(String(deadline).replace(" ", "T")).getTime();
                if (!Number.isNaN(deadlineTime) && deadlineTime <= Date.now()) {
                    refreshed = true;
                    fetchRakeback();
                }
            }
        };

        updateTimer();
        const timerId = window.setInterval(updateTimer, 1000);
        return () => window.clearInterval(timerId);
    }, [open, rakebackData.daily?.claimDeadline]);

    return (
        <>
            <Grid item xs={6} sm={6} md={3}>
                <Card
                    className="c_card middle_card"
                    onClick={() => setOpen(true)}
                    sx={{
                        position: "relative",
                        cursor: "pointer",
                        background: theme.palette.custom.lgray,
                        borderColor: theme.palette.custom.redblue_border,
                    }}
                >
                    <div className="card_label">
                        <p>
                            <span style={{ fontSize: "10px", color: "white" }}>{t('rakeback.cardBadge')}</span>
                        </p>
                        <img src={tagIcon} alt={t('rakeback.labelIconAlt')} />
                    </div>

                    <div className="buy_card1">
                        <div className="right_half">
                            {banner ? (
                                <img
                                    src={`${endPoints?.imageCDN}${banner}`}
                                    alt={t('rakeback.title')}
                                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: 88,
                                        height: 88,
                                        mx: "auto",
                                        mb: 1,
                                        borderRadius: "50%",
                                        background:
                                            "radial-gradient(circle at 35% 28%, #d6eaff, #2d66aa 58%, #15375d)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 14px 28px rgba(0, 0, 0, 0.28)",
                                    }}
                                >
                                    <AccountBalanceWalletOutlinedIcon sx={{ color: "#fff", fontSize: 42 }} />
                                </Box>
                            )}
                        </div>

                        <div className="left_half">
                            <Typography
                                className="f18 f-bold"
                                sx={{
                                    color: theme.palette.custom.white_gray,
                                    textAlign: "center",
                                    px: 1,
                                }}
                            >
                                {t('rakeback.title')}
                            </Typography>

                            <Button
                                className="best_btn"
                                size="small"
                                sx={{
                                    background: theme.palette.custom.red,
                                    color: theme.palette.custom.white,
                                    textTransform: "none",
                                }}
                            >
                                {t('rakeback.viewDetails')}
                            </Button>
                        </div>
                    </div>
                </Card>
            </Grid>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{ zIndex: 10101 }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        width: { xs: "calc(100% - 24px)", sm: 560 },
                        maxHeight: "calc(100vh - 40px)",
                        background: "#fff",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                    },
                }}
            >
                <IconButton
                    onClick={() => setOpen(false)}
                    sx={{
                        position: "absolute",
                        right: 12,
                        top: 12,
                        zIndex: 2,
                        color: "#111",
                        background: "rgba(17, 17, 17, 0.06)",
                        "&:hover": { background: "rgba(17, 17, 17, 0.1)" },
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent
                    sx={{
                        p: 0,
                        overflowY: "auto",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 1.25, sm: 1.5 },
                            backgroundColor: "#f1eee6",
                        }}
                    >
                        <Box
                            sx={{
                                overflow: "hidden",
                                borderRadius: 2.5,
                                backgroundColor: "#fff",
                                border: "1px solid #cfcac0",
                            }}
                        >
                            <Box
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    pr: { xs: 6, sm: 6 },
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: "#111", fontSize: 24, fontWeight: 800, lineHeight: 1.15 }}>
                                        {t('rakeback.title')}
                                    </Typography>
                                    <Typography sx={{ color: "#555", fontSize: 14, mt: 0.35 }}>
                                        {t('rakeback.tierName', { tier: currentTierName })}
                                    </Typography>
                                </Box>
                                <Chip
                                    icon={<AccountBalanceWalletOutlinedIcon />}
                                    label={showTierRates ? t('rakeback.back') : headerStatus}
                                    size="small"
                                    onClick={() => setShowTierRates((prev) => !prev)}
                                    sx={{
                                        color: "#132338",
                                        backgroundColor: "#eaf2ff",
                                        cursor: "pointer",
                                        fontSize: 14,
                                        height: 34,
                                        px: 0.5,
                                        "& .MuiChip-icon": { color: "#132338" },
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    color: "#fff",
                                    background:
                                        "radial-gradient(circle at 15% 10%, rgba(70, 132, 203, 0.28), transparent 34%), linear-gradient(135deg, #0d2138, #163a5c)",
                                }}
                            >
                                <Typography sx={{ color: "#acd2e6", fontSize: 13, textTransform: "uppercase", letterSpacing: 0 }}>
                                    {t('rakeback.availableToClaim')}
                                </Typography>
                                <Typography sx={{ fontSize: { xs: 46, sm: 52 }, fontWeight: 800, lineHeight: 1.05, mt: 1 }}>
                                    {loading ? "..." : formatMoney(dailyAmount)}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1.25 }}>
                                    {rakebackData.daily?.claimDeadline && resetTimerLabel ? (
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 0.85,
                                                px: 1.35,
                                                py: 0.8,
                                                borderRadius: 1.5,
                                                color: "#201500",
                                                background: "linear-gradient(135deg, #ffdd68, #f2a914)",
                                                border: "1px solid rgba(255, 244, 190, 0.82)",
                                                boxShadow: "0 0 0 3px rgba(242, 169, 20, 0.2), 0 12px 26px rgba(0, 0, 0, 0.22)",
                                                maxWidth: "100%",
                                            }}
                                        >
                                            <AccessTimeOutlinedIcon sx={{ fontSize: 21, color: "#201500", flex: "0 0 auto" }} />
                                            <Typography sx={{ fontSize: { xs: 15, sm: 17 }, fontWeight: 800, color: "inherit", letterSpacing: 0 }}>
                                                {resetTimerLabel}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography sx={{ color: "#9fb8cc", fontSize: 14 }}>
                                            {t('rakeback.claimEvery24Hours')}
                                        </Typography>
                                    )}
                                </Box>
                                {hasNoDailyGameplay && (
                                    <Typography sx={{ color: "#f7d68a", fontSize: 12, mt: 0.75, lineHeight: 1.35 }}>
                                        {t('rakeback.playToEarn')}
                                    </Typography>
                                )}
                                <Button
                                    fullWidth
                                    onClick={handleClaim}
                                    disabled={isAuthenticated && (!canSubmitClaim || claiming || loading)}
                                    sx={{
                                        mt: 3,
                                        py: 1.35,
                                        color: "#fff",
                                        fontWeight: 800,
                                        fontSize: 16,
                                        textTransform: "none",
                                        borderRadius: 1.5,
                                        background: "linear-gradient(135deg, #315f9c, #294f83)",
                                        boxShadow: "none",
                                        "&:hover": { background: "linear-gradient(135deg, #3970b5, #2d5b98)" },
                                        "&.Mui-disabled": {
                                            color: "rgba(255,255,255,0.62)",
                                            background: "rgba(49, 95, 156, 0.62)",
                                        },
                                    }}
                                >
                                    {claimButtonLabel}
                                </Button>
                            </Box>

                            <Box sx={{ backgroundColor: "#fff", color: "#111" }}>
                                {showTierRates ? (
                                    <TierRatesPanel currentTierName={currentTierName} tiers={tierRows} />
                                ) : (
                                    <>
                                        <Tabs
                                            value={activeTab}
                                            onChange={(_, nextTab) => setActiveTab(nextTab)}
                                            variant="fullWidth"
                                            sx={{
                                                minHeight: 46,
                                                borderBottom: "1px solid #dedede",
                                                "& .MuiTab-root": {
                                                    color: "#444",
                                                    minHeight: 46,
                                                    fontSize: 14,
                                                    textTransform: "none",
                                                },
                                                "& .Mui-selected": { color: "#111 !important", fontWeight: 700 },
                                                "& .MuiTabs-indicator": { backgroundColor: "#111", height: 3 },
                                            }}
                                        >
                                            {Object.keys(buildTabData).map((tab) => (
                                                <Tab key={tab} value={tab} label={t(TAB_LABEL_KEYS[tab])} />
                                            ))}
                                        </Tabs>

                                        <Box sx={{ p: { xs: 1.75, sm: 2 } }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2 }}>
                                                <Typography sx={{ fontSize: 14 }}>{activeData.periodLabel}</Typography>
                                                <Typography sx={{ fontSize: 14, textAlign: "right" }}>
                                                    {activeData.periodValue}
                                                </Typography>
                                            </Box>

                                            {activeTab === "daily" ? (
                                                <Box>
                                                    {activeData.metrics.map((metric) => (
                                                        <Box
                                                            key={metric[0]}
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                py: 0.5,
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <Typography sx={{ fontSize: 14 }}>{metric[0]}</Typography>
                                                            <Typography sx={{ fontSize: 14 }}>{metric[1]}</Typography>
                                                        </Box>
                                                    ))}
                                                    {nextTierUpgrade && (
                                                        <Box
                                                            sx={{
                                                                mt: 1.5,
                                                                p: 1.25,
                                                                borderRadius: 2,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                color: "#0b5d9f",
                                                                background: "linear-gradient(135deg, #eaf4ff, #f3f9ff)",
                                                                border: "1px solid #cfe3f7",
                                                            }}
                                                        >
                                                            <AccountBalanceWalletOutlinedIcon fontSize="small" />
                                                            <Typography sx={{ fontSize: 13, lineHeight: 1.4 }}>
                                                                {t('rakeback.tierUpgradeNotice', {
                                                                    tier: nextTierUpgrade.name,
                                                                    rate: formatPercent(nextTierUpgrade.rate),
                                                                    period: nextTierUpgrade.periodLabel,
                                                                    amount: formatMoney(nextTierUpgrade.nextEarned),
                                                                })}
                                                                {nextTierUpgrade.delta > 0 && (
                                                                    <> {t('rakeback.tierUpgradeDelta', { delta: formatMoney(nextTierUpgrade.delta) })}</>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {dailySummary.carryForward > 0 && (
                                                        <Box
                                                            sx={{
                                                                mt: 1.5,
                                                                p: 1.25,
                                                                borderRadius: 2,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                color: "#8a5a00",
                                                                background: "linear-gradient(135deg, #fff6df, #fdefcf)",
                                                                border: "1px solid #f0dca8",
                                                            }}
                                                        >
                                                            <AccountBalanceWalletOutlinedIcon fontSize="small" />
                                                            <Typography sx={{ fontSize: 13, lineHeight: 1.4 }}>
                                                                {t('rakeback.carriedForwardNotice', {
                                                                    amount: formatMoney(dailySummary.carryForward),
                                                                })}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {dailySummary.pendingRakeback > 0 && (
                                                        <Box
                                                            sx={{
                                                                mt: 1.5,
                                                                p: 1.25,
                                                                borderRadius: 2,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                color: "#8a5a00",
                                                                background: "linear-gradient(135deg, #fff6df, #fdefcf)",
                                                                border: "1px solid #f0dca8",
                                                            }}
                                                        >
                                                            <AccessTimeOutlinedIcon fontSize="small" />
                                                            <Typography sx={{ fontSize: 13, lineHeight: 1.4 }}>
                                                                {t('rakeback.pendingRakebackNotice', {
                                                                    amount: formatMoney(dailySummary.pendingRakeback),
                                                                })}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                                    {activeData.cards.map((item) => (
                                                        <StatCard key={item[0]} item={item} />
                                                    ))}
                                                    {nextTierUpgrade && (
                                                        <Box
                                                            sx={{
                                                                width: "100%",
                                                                p: 1.25,
                                                                borderRadius: 2,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                color: "#0b5d9f",
                                                                background: "linear-gradient(135deg, #eaf4ff, #f3f9ff)",
                                                                border: "1px solid #cfe3f7",
                                                            }}
                                                        >
                                                            <AccountBalanceWalletOutlinedIcon fontSize="small" />
                                                            <Typography sx={{ fontSize: 13, lineHeight: 1.4 }}>
                                                                {t('rakeback.tierUpgradeNotice', {
                                                                    tier: nextTierUpgrade.name,
                                                                    rate: formatPercent(nextTierUpgrade.rate),
                                                                    period: nextTierUpgrade.periodLabel,
                                                                    amount: formatMoney(nextTierUpgrade.nextEarned),
                                                                })}
                                                                {nextTierUpgrade.delta > 0 && (
                                                                    <> {t('rakeback.tierUpgradeDelta', { delta: formatMoney(nextTierUpgrade.delta) })}</>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}

                                            <Divider sx={{ my: 2 }} />

                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: "#f5f2ea",
                                                    border: "1px solid #e5ded0",
                                                }}
                                            >
                                                {activeData.rows.map((row) => (
                                                    <DetailRow key={row[0]} row={row} />
                                                ))}
                                            </Box>

                                            {activeTab === "weekly" && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography sx={{ fontSize: 16, mb: 1.5 }}>{t('rakeback.thisWeek')}</Typography>
                                                    {activeData.days.map((day) => (
                                                        <Box
                                                            key={day.key}
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                gap: 2,
                                                                py: 1,
                                                                borderBottom: "1px solid #d8d2c7",
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography sx={{ fontSize: 14 }}>{day.dateLabel}</Typography>
                                                                <Typography sx={{ color: "#333", fontSize: 13 }}>{day.subtitle}</Typography>
                                                            </Box>
                                                            <Box sx={{ textAlign: "right" }}>
                                                                {day.lines.map((line, lineIndex) => (
                                                                    <Box key={`${line.label}-${line.amount}`} sx={{ mt: lineIndex === 0 ? 0 : 0.75 }}>
                                                                        <Typography sx={{ color: line.amount.startsWith("+") ? "#159063" : "#555" }}>
                                                                            {line.amount}
                                                                        </Typography>
                                                                        {line.label && (
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 700,
                                                                                    color: line.isPositive ? "#159063" : "#d32f2f",
                                                                                }}
                                                                            >
                                                                                {line.label}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {activeTab === "monthly" && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography sx={{ fontSize: 16, mb: 1.5 }}>{t('rakeback.dailyBreakdown')}</Typography>
                                                    {activeData.days.length === 0 ? (
                                                        <Typography sx={{ color: "#666", fontSize: 13 }}>
                                                            {t('rakeback.noMonthlyActivity')}
                                                        </Typography>
                                                    ) : (
                                                        <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
                                                            {activeData.days.map((day) => (
                                                                <Box
                                                                    key={day.key}
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        gap: 2,
                                                                        py: 1,
                                                                        borderBottom: "1px solid #d8d2c7",
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography sx={{ fontSize: 14 }}>{day.dateLabel}</Typography>
                                                                        <Typography sx={{ color: "#333", fontSize: 13 }}>{day.subtitle}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ textAlign: "right" }}>
                                                                        {day.lines.map((line, lineIndex) => (
                                                                            <Box key={`${line.label}-${line.amount}`} sx={{ mt: lineIndex === 0 ? 0 : 0.75 }}>
                                                                                <Typography sx={{ color: line.amount.startsWith("+") ? "#159063" : "#555" }}>
                                                                                    {line.amount}
                                                                                </Typography>
                                                                                {line.label && (
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontSize: 12,
                                                                                            fontWeight: 700,
                                                                                            color: line.isPositive ? "#159063" : "#d32f2f",
                                                                                        }}
                                                                                    >
                                                                                        {line.label}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}

                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    color: activeTab === "daily" && !canClaim ? "#5f4100" : "#0a5b3e",
                                                    background: activeTab === "daily" && !canClaim
                                                        ? "linear-gradient(135deg, #fff5d9, #f5ead0)"
                                                        : "linear-gradient(135deg, #e4fff3, #d9f2eb)",
                                                    display: "flex",
                                                    gap: 1,
                                                }}
                                            >
                                                <CheckBoxOutlinedIcon fontSize="small" />
                                                <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
                                                    {activeData.notice}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RakebackPromotion;
